import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API, { apis } from "common/apis";
import { IApiError } from "common/interface/IApiError";
import { logoutIfNeeded } from "common/utils";
import {
  getPersistedProcessingFifDsr,
  persistProcessingFifDsr,
  removePersistedProcessingFifDsr,
} from "services/PersistService/PersistFifDsrService";
import { RootState } from "store/store";

export interface IFifDsrUpload {
  key?: string;
  headerMapping?: string;
  successRows?: any[];
  failedRows?: any[];
  timeUploaded?: string;
  [key: string]: any;
}

export interface State {
  processingFifDsr: IFifDsrUpload;
  status: "loading" | "success" | "error" | "idle" | "cancelling" | "saving" | "creating" | "saved";
  error?: string;
}

const initialState: State = {
  processingFifDsr: getPersistedProcessingFifDsr(),
  status: "idle",
};

export const cancelProcessingFifDsr = createAsyncThunk(
  "fifDsr/cancel",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const {
      fifDsr: {
        processingFifDsr: { key },
      },
    } = state;

    const response = await API.post(
      apis.saveFifDsr,
      {},
      {
        params: { key, cancel: 1 },
      }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

export const saveProcessingFifDsr = createAsyncThunk<IFifDsrUpload>(
  "fifDsr/upload/save",
  async (_, thunkApi) => {
    const state = thunkApi.getState() as RootState;
    const {
      fifDsr: {
        processingFifDsr: { key },
      },
    } = state;

    const response = await API.post(
      apis.saveFifDsr,
      {},
      {
        params: { key },
      }
    )
      .then((success) => success.data)
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message || "Something went wrong!";
        logoutIfNeeded(error, thunkApi);
        return thunkApi.rejectWithValue({ message: errorMessage });
      });

    return response;
  }
);

const fifDsrSlice = createSlice({
  name: "fifDsr",
  initialState,
  reducers: {
    setProcessingFifDsr: (state, action) => {
      state.processingFifDsr = action.payload;
      persistProcessingFifDsr(action.payload);
    },
    removeProcessingFifDsr: (state) => {
      state.processingFifDsr = {};
      state.status = "idle";
      removePersistedProcessingFifDsr();
    },
  },
  extraReducers(builder) {
    builder
      .addCase(saveProcessingFifDsr.pending, (state) => {
        state.status = "loading";
        state.error = undefined;
      })
      .addCase(saveProcessingFifDsr.fulfilled, (state) => {
        state.status = "success";
        state.processingFifDsr = {};
        removePersistedProcessingFifDsr();
      })
      .addCase(saveProcessingFifDsr.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message || action.error.message || "Something went wrong!";
      })
      .addCase(cancelProcessingFifDsr.pending, (state) => {
        state.status = "cancelling";
        state.error = undefined;
      })
      .addCase(cancelProcessingFifDsr.fulfilled, (state) => {
        state.status = "idle";
        state.processingFifDsr = {};
        removePersistedProcessingFifDsr();
      })
      .addCase(cancelProcessingFifDsr.rejected, (state, action) => {
        const error = action.payload as IApiError;
        state.status = "error";
        state.error = error?.message || action.error.message || "Something went wrong!";
      });
  }
});

export const { setProcessingFifDsr, removeProcessingFifDsr } = fifDsrSlice.actions;
export default fifDsrSlice.reducer;
